import * as THREE from 'three';
import { el } from '@/ui/dom';
import { brandLogo } from '@/ui/brandLogo';
import { cssColor } from '@/ui/format';
import { citySilhouette } from '@/ui/citySilhouettes';
import { CITIES, type City } from '@/config/cities';
import type { GlobeSpinner } from '@/world/Globe';
import { reportError } from '@/dev/errorlog';

/**
 * RaceIntroScreen.ts — the M63 lobby→race transition. The referee holds a
 * short STAGING beat ({t:'begin'}) before the countdown, and this full-screen
 * overlay plays over it:
 *   - single race: the city splash — art, huge italic name, country, and a
 *     pulsing "START YOUR ENGINES". The art slot first tries a real photo at
 *     public/cities/<id>.webp (the owner's photoreal drop-in path); until one
 *     exists the procedural night-sky skyline (the city-card art language)
 *     fills the screen.
 *   - tour: the DRIBBLE RACER WORLD TOUR CUP splash — gold title treatment
 *     over a slowly rotating dot-matrix Earth with every tour city glowing
 *     (the M9 globe, reused via createGlobeSpinner; lazy chunk).
 * Timed to the server's staging window so the 3-2-1 countdown is still fully
 * in view when this fades. Tap anywhere to skip (client-side only).
 */

/** Minimum visible time — roughly net/lobby LOBBY_STAGING_MS. The fade-out is
 *  additionally GATED on the race actually running (Flow calls raceReady() on
 *  the first snapshot), so the reveal always lands on the live countdown —
 *  never a flash of the stale lobby before a hard cut (M65 smoothing). */
const INTRO_MIN_VISIBLE_MS = 3100;
const INTRO_FADE_MS = 700;
/** If the race never materialises (connection trouble), fade anyway. */
const INTRO_FAILSAFE_MS = 9000;
/** Loading round: offline, if the world never reports ready (a warm-up that
 *  hung past all its own caps), release anyway rather than strand the
 *  player on the splash — the caps add up to ~20 s, so this is a last line. */
const INTRO_READY_CAP_MS = 30000;

/** M158c — the loading pitch's ball (panels drawn flat, it rolls by rotation). */
const LOAD_BALL_SVG = `<svg viewBox="0 0 40 40" aria-hidden="true">
<defs><radialGradient id="ldb" cx="38%" cy="32%" r="70%"><stop offset="0" stop-color="#fff"/><stop offset="0.7" stop-color="#e9e6dc"/><stop offset="1" stop-color="#9c978a"/></radialGradient></defs>
<circle cx="20" cy="20" r="19" fill="url(#ldb)" stroke="#2b2620" stroke-width="1"/>
<polygon points="20,12 27.6,17.5 24.7,26.5 15.3,26.5 12.4,17.5" fill="#1d1b19"/>
<g fill="#1d1b19"><path d="M20 1.2 L25 4.2 L20 7.4 L15 4.2 Z"/><path d="M37.6 15 L38.6 21.4 L33.6 19.6 L33.4 13.8 Z"/>
<path d="M30.8 34.6 L25.6 38 L25.2 32.4 L29.6 29.6 Z"/><path d="M9.2 34.6 L14.4 38 L14.8 32.4 L10.4 29.6 Z"/><path d="M2.4 15 L1.4 21.4 L6.4 19.6 L6.6 13.8 Z"/></g>
<g stroke="#2b2620" stroke-width="0.8" fill="none"><path d="M20 7.4 L20 12 M27.6 17.5 L33.6 19.6 M24.7 26.5 L29.6 29.6 M15.3 26.5 L10.4 29.6 M12.4 17.5 L6.4 19.6"/></g>
</svg>`;

/** M158c — the goal at the end of the loading pitch (posts, bar and net). */
const LOAD_GOAL_SVG = `<svg viewBox="0 0 52 40" aria-hidden="true">
<g class="raceintro-net" stroke="rgba(255,255,255,0.55)" stroke-width="0.7" fill="none">
<path d="M8 6 L44 11 L44 38 L8 38"/><path d="M14 7 L14 38 M20 7.8 L20 38 M26 8.6 L26 38 M32 9.4 L32 38 M38 10.2 L38 38"/>
<path d="M8 14 L44 18 M8 22 L44 25 M8 30 L44 32"/></g>
<path d="M6 39 L6 5 L46 5 L46 9" stroke="#fff" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M4 39 L48 39" stroke="#ffd34a" stroke-width="1.6"/>
</svg>`;

/** Shared chip icons (24×24 viewBox) used by both intro faces. */
const ICON_GLOBE =
  '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.7"/>' +
  '<ellipse cx="12" cy="12" rx="4.2" ry="9" fill="none" stroke="currentColor" stroke-width="1.7"/>' +
  '<path d="M3.6 8.6h16.8M3.6 15.4h16.8" fill="none" stroke="currentColor" stroke-width="1.7"/>';
const ICON_HELMET =
  '<path d="M12 4a8 8 0 0 0-8 8v4.5A1.5 1.5 0 0 0 5.5 18H10l-1.6-3.4A8.3 8.3 0 0 1 20 12.3 8 8 0 0 0 12 4Zm8 10h-7.4l1.9 4H18a2 2 0 0 0 2-2v-2Z" fill="currentColor"/>';
const ICON_CUP =
  '<path d="M7 3h10v2h3v3c0 2.4-1.8 4.3-4.1 4.4A5 5 0 0 1 13 15v2h3v3H8v-3h3v-2a5 5 0 0 1-2.9-2.6C5.8 12.3 4 10.4 4 8V5h3V3Z" fill="currentColor"/>';
const ICON_GAUGE =
  '<path d="M4 17a8 8 0 1 1 16 0" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
  '<path d="M12 16l4-4.5" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>' +
  '<circle cx="12" cy="16" r="1.4" fill="currentColor"/>';
const ICON_PIN =
  '<path d="M12 2a6.5 6.5 0 0 0-6.5 6.5C5.5 13.6 12 21 12 21s6.5-7.4 6.5-12.5A6.5 6.5 0 0 0 12 2Zm0 9a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" fill="currentColor"/>';

export class RaceIntroScreen {
  private root: HTMLDivElement;
  private hideTimer: number | null = null;
  private fadeTimer: number | null = null;
  private failTimer: number | null = null;
  private minTimeReached = false;
  private raceStarted = false;
  private fading = false;
  /** OFFLINE ONLY (M67): there's no server staging beat, so once the intro has
   *  been visible for its minimum time we call this to START the countdown, then
   *  fade onto it. Online leaves this null and drives raceReady() externally. */
  private onRelease: (() => void) | null = null;
  /** Loading round (offline): the world + warm-up are done — the release may
   *  happen (after the min time, or on a tap). Online this just flips the
   *  status line; the server's first snapshot (raceReady) drives the fade. */
  private ready = false;
  /** The offline release has fired (beginRace ran) — or online, the race is
   *  live. Guards against a double release. */
  private released = false;
  /** Loading mode (the splash doubles as the loading screen): the bar +
   *  status line reflect build/warm-up progress until setReady(). */
  private loadingMode = false;
  private progressFrac = 0;
  private progressLabel = '';
  private bar: HTMLDivElement | null = null;
  private barFill: HTMLDivElement | null = null;
  private barLabel: HTMLDivElement | null = null;
  private barPct: HTMLDivElement | null = null;
  private barBall: HTMLDivElement | null = null;
  private engines: HTMLDivElement | null = null;
  private readyTimer: number | null = null;
  // Tour globe bits — one small persistent renderer (created on first tour
  // intro, reused after; never churn WebGL contexts).
  private renderer: THREE.WebGLRenderer | null = null;
  private spinner: GlobeSpinner | null = null;
  private raf: number | null = null;
  private lastT = 0;
  private readonly reducedMotion =
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'screen screen-raceintro', parent);
    this.root.style.display = 'none';
    this.root.addEventListener('click', () => this.onTap());
  }

  /** Single race: the city splash. `opts.kicker` overrides the top label
   *  (default "ONLINE RACE"; offline quick races pass "QUICK RACE"); `opts.onRelease`
   *  is the offline countdown-starter (see the field above). */
  showCity(
    cityId: string,
    opts: {
      kicker?: string;
      racers?: number;
      difficulty?: string;
      onRelease?: () => void;
      /** Loading round: start in loading mode (progress bar + status) —
       *  the caller drives setProgress()/setReady(). */
      loading?: boolean;
    } = {},
  ): void {
    const city = CITIES.find((c) => c.id === cityId) ?? CITIES[0];
    this.prepare();
    this.onRelease = opts.onRelease ?? null;
    this.loadingMode = !!opts.loading;
    this.ready = !!this.onRelease && !this.loadingMode; // nothing to wait for → tap = start
    this.root.classList.remove('raceintro-tour');
    this.root.style.setProperty('--intro-accent', cssColor(city.theme.roadLine));

    // Full-bleed city art backdrop (procedural skyline, or a photo drop-in).
    const art = el('div', 'raceintro-art', this.root);
    art.innerHTML = `<span class="raceintro-sil">${citySilhouette(city.id)}</span>`;
    this.tryPhoto(art, city);

    // Editorial title block — mirrors the tour intro's polish (rule-kicker,
    // metallic display name, icon chips, corner captions), tinted the city accent.
    const text = el('div', 'raceintro-text raceintro-citytext', this.root);

    const kick = el('div', 'raceintro-cupkicker', text);
    el('span', 'raceintro-rule', kick);
    el('span', 'raceintro-cupkicker-text', kick).textContent = opts.kicker ?? 'ONLINE RACE';
    el('span', 'raceintro-rule', kick);

    el('div', 'raceintro-city', text).textContent = city.name.toUpperCase();

    const chips = el('div', 'raceintro-chips raceintro-citychips', text);
    const chip = (icon: string, label: string): void => {
      const c = el('span', 'raceintro-chip', chips);
      c.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${icon}</svg><span>${label}</span>`;
    };
    chip(ICON_PIN, city.country.toUpperCase());
    if (opts.racers) chip(ICON_HELMET, `${opts.racers} RACERS`);
    if (opts.difficulty) chip(ICON_GAUGE, opts.difficulty.toUpperCase());

    this.engines = el('div', 'raceintro-engines', text);
    this.buildProgress(text);

    el('div', 'raceintro-corner raceintro-corner-l', this.root).textContent = 'BUILT FOR SPEED';
    el('div', 'raceintro-corner raceintro-corner-r', this.root).textContent = 'TAKE THE LEAD';

    this.armTimers();
    this.paintStatus();
  }

  /** Tour: the World Tour Cup splash (M65 redesign to the owner's reference):
   *  the brand block — the owner's logo art (M148), rules, stat chips —
   *  beside the black-and-gold planet, with corner captions. `route` (M87):
   *  the spinner flies THIS tour's rolled city order (offline passes the
   *  save's order; online keeps the canonical ring — the route lives on the
   *  server there). */
  showTour(
    tourLen: number,
    raceNum: number,
    racers = 6,
    onRelease?: () => void,
    route?: City[],
    loading = false,
  ): void {
    this.prepare();
    this.onRelease = onRelease ?? null;
    this.loadingMode = loading;
    this.ready = !!this.onRelease && !this.loadingMode;
    this.root.classList.add('raceintro-tour');
    this.root.style.setProperty('--intro-accent', '#ffd34a');

    // The globe sits BEHIND now (dimmed), and the Blender trophy is the hero
    // on the right (M66). A missing trophy image just leaves the globe.
    const globeHost = el('div', 'raceintro-globe', this.root);
    void this.mountGlobe(globeHost, route);

    const trophy = document.createElement('img');
    trophy.className = 'raceintro-trophy';
    trophy.alt = '';
    trophy.addEventListener('load', () => this.root.classList.add('has-trophy'));
    trophy.addEventListener('error', () => trophy.remove());
    trophy.src = `${import.meta.env.BASE_URL}lobby/trophy.webp`;
    this.root.appendChild(trophy);

    const text = el('div', 'raceintro-text raceintro-tourtext', this.root);
    const kicker = el('div', 'raceintro-cupkicker', text);
    el('span', 'raceintro-rule', kicker);
    // M148: the logo art carries WORLD TOUR CUP itself, so the kicker names
    // the stop instead of repeating it.
    el('span', 'raceintro-cupkicker-text', kicker).textContent = `RACE ${raceNum} OF ${tourLen}`;
    el('span', 'raceintro-rule', kicker);

    brandLogo(text, 'raceintro-brandlogo');

    const chips = el('div', 'raceintro-chips', text);
    const chip = (icon: string, label: string): void => {
      const c = el('span', 'raceintro-chip', chips);
      c.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${icon}</svg><span>${label}</span>`;
    };
    chip(ICON_GLOBE, `${tourLen} CITIES`);
    chip(ICON_HELMET, `${racers} RACERS`);
    chip(ICON_CUP, '1 CHAMPION');

    this.engines = el('div', 'raceintro-engines', text);
    this.buildProgress(text);

    el('div', 'raceintro-corner raceintro-corner-l', this.root).textContent = 'BUILT FOR SPEED';
    el('div', 'raceintro-corner raceintro-corner-r', this.root).textContent = 'PLAY FOR GLORY';

    this.armTimers();
    this.paintStatus();
  }

  /** Blank the panel + show it (shared by both faces). */
  private prepare(): void {
    this.clearTimers();
    this.stopGlobe();
    this.root.replaceChildren();
    this.root.classList.remove('raceintro-out', 'raceintro-ready', 'raceintro-nudge');
    this.root.style.display = '';
    this.ready = false;
    this.released = false;
    this.loadingMode = false;
    this.progressFrac = 0;
    this.progressLabel = '';
    this.bar = this.barFill = this.barLabel = this.barPct = this.barBall = this.engines = null;
    this.onVisibleChange?.(true);
  }

  /** Loading round: the progress strip under the status line — a gold bar
   *  with a travelling sheen, a phase label and a percentage. Built on both
   *  faces; only shown in loading mode (CSS hides it otherwise). */
  private buildProgress(parent: HTMLElement): void {
    this.bar = el('div', 'raceintro-progress', parent);
    // M158c (owner): the bar is a little pitch — a soccer ball rolls along it
    // toward a goal as the race loads, and scores when the race is READY.
    const pitch = el('div', 'raceintro-pitch', this.bar);
    const track = el('div', 'raceintro-progress-track', pitch);
    this.barFill = el('div', 'raceintro-progress-fill', track);
    el('div', 'raceintro-progress-sheen', this.barFill);
    el('div', 'raceintro-goal', pitch).innerHTML = LOAD_GOAL_SVG;
    this.barBall = el('div', 'raceintro-ball', pitch);
    el('div', 'raceintro-ball-spin', this.barBall).innerHTML = LOAD_BALL_SVG;
    const row = el('div', 'raceintro-progress-row', this.bar);
    this.barLabel = el('div', 'raceintro-progress-label', row);
    this.barPct = el('div', 'raceintro-progress-pct', row);
    this.root.classList.toggle('raceintro-loading', this.loadingMode);
  }

  /** Loading round: drive the bar (0..1) + its phase label. Safe to call
   *  before show() (the value is kept and painted on the next show). */
  setProgress(frac: number, label?: string): void {
    this.progressFrac = Math.max(this.progressFrac, Math.min(1, Math.max(0, frac)));
    if (label !== undefined) this.progressLabel = label;
    this.paintProgress();
  }

  /** Loading round: the world is built + warm. Offline, the countdown may
   *  now start (after the min time — or at once on a tap); online the status
   *  flips to START YOUR ENGINES while the server's start drives the fade. */
  setReady(): void {
    if (this.ready) return;
    this.ready = true;
    this.progressFrac = 1;
    this.root.classList.add('raceintro-ready');
    if (this.readyTimer !== null) {
      window.clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
    this.paintProgress();
    this.paintStatus();
    this.tryRelease();
  }

  /** Is the intro currently on screen? */
  get isVisible(): boolean {
    return this.root.style.display !== 'none';
  }

  /** Loading round: Flow pauses the attract-world render under the opaque
   *  splash (GPU waste, and it would move the camera the warm render uses). */
  onVisibleChange: ((visible: boolean) => void) | null = null;

  private paintProgress(): void {
    if (!this.barFill) return;
    const pct = Math.round(this.progressFrac * 100);
    this.barFill.style.width = `${pct}%`;
    this.bar?.style.setProperty('--p', this.progressFrac.toFixed(3));
    if (this.barPct) this.barPct.textContent = `${pct}%`;
    if (this.barLabel) this.barLabel.textContent = this.ready ? 'READY' : this.progressLabel;
  }

  private paintStatus(): void {
    if (!this.engines) return;
    if (!this.loadingMode) {
      this.engines.textContent = 'START YOUR ENGINES';
      return;
    }
    if (!this.ready) this.engines.textContent = 'WARMING UP THE ENGINES';
    else if (this.onRelease && !this.released) this.engines.textContent = 'TAP TO RACE';
    else this.engines.textContent = 'START YOUR ENGINES';
  }

  /** Offline: fire the countdown-starter exactly once, when the world is
   *  ready AND (the min time has passed OR the player tapped). */
  private tryRelease(force = false): void {
    if (this.released || !this.onRelease) return;
    if (!this.ready) return;
    if (!this.minTimeReached && !force) return;
    this.released = true;
    const release = this.onRelease;
    this.onRelease = null;
    try {
      release();
    } catch (e) {
      // A throw here used to strand the player on the attract flythrough
      // (loading round audit) — surface it and still tear down cleanly.
      console.error('[RaceIntro] race start failed', e);
      reportError('raceintro.release', e);
    }
    this.paintStatus();
    this.maybeFade();
  }

  /** A tap on the splash. Offline: START NOW if the world is ready, else a
   *  gentle nudge (nothing to skip to yet — the old behaviour dropped the
   *  start callback and left the player on the attract flythrough with no
   *  way out). Online: skip the art (the server starts the race regardless). */
  private onTap(): void {
    if (this.onRelease) {
      if (this.ready) {
        this.tryRelease(true);
        return;
      }
      this.root.classList.remove('raceintro-nudge');
      void this.root.offsetWidth;
      this.root.classList.add('raceintro-nudge');
      return;
    }
    this.hide();
  }

  /** The photoreal drop-in path: if public/cities/<id>.webp exists it replaces
   *  the procedural skyline; a missing file just never shows (onerror). */
  private tryPhoto(art: HTMLElement, city: City): void {
    const img = document.createElement('img');
    img.className = 'raceintro-photo';
    img.alt = '';
    img.addEventListener('load', () => art.classList.add('has-photo'));
    img.addEventListener('error', () => img.remove());
    img.src = `${import.meta.env.BASE_URL}cities/${city.id}.webp`;
    art.appendChild(img);
  }

  private async mountGlobe(host: HTMLElement, route?: City[]): Promise<void> {
    try {
      const { createGlobeSpinner } = await import('@/world/Globe');
      if (this.root.style.display === 'none') return; // skipped before the chunk landed
      if (!this.renderer) {
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.domElement.className = 'raceintro-canvas';
      }
      host.appendChild(this.renderer.domElement);
      const w = Math.max(1, host.clientWidth);
      const h = Math.max(1, host.clientHeight);
      this.renderer.setSize(w, h);
      this.spinner = createGlobeSpinner(route && route.length >= 2 ? route : CITIES);
      this.spinner.setAspect(w / h);
      this.lastT = performance.now();
      const tick = (): void => {
        this.raf = requestAnimationFrame(tick);
        const now = performance.now();
        const dt = Math.min(0.1, (now - this.lastT) / 1000);
        this.lastT = now;
        if (this.spinner && this.renderer) {
          if (!this.reducedMotion) this.spinner.update(dt);
          this.renderer.render(this.spinner.scene, this.spinner.camera);
        }
      };
      this.raf = requestAnimationFrame(tick);
    } catch (e) {
      // The gold text splash stands alone — but say why, so a broken globe
      // can't fail silently forever (M65 debugging lesson).
      console.warn('[RaceIntro] tour globe failed to mount:', e);
    }
  }

  private armTimers(): void {
    this.minTimeReached = false;
    this.raceStarted = false;
    this.fading = false;
    this.hideTimer = window.setTimeout(() => {
      this.minTimeReached = true;
      // Offline: start the countdown now (if the world is ready), then fade
      // onto it. A non-loading offline intro (legacy callers) is ready at once.
      if (this.onRelease && !this.loadingMode) this.ready = true;
      this.tryRelease();
      this.maybeFade();
    }, INTRO_MIN_VISIBLE_MS);
    // Online only: if the race never materialises (connection trouble), fade
    // anyway. Offline the release itself gates the fade, so a missing world
    // never silently fades the splash away over the attract flythrough.
    this.failTimer = window.setTimeout(() => {
      if (this.onRelease) return;
      this.minTimeReached = true;
      this.raceStarted = true;
      this.maybeFade();
    }, INTRO_FAILSAFE_MS);
    if (this.onRelease && this.loadingMode) {
      this.readyTimer = window.setTimeout(() => {
        if (!this.ready) {
          console.warn('[RaceIntro] world never reported ready — releasing anyway');
          this.setReady();
        }
      }, INTRO_READY_CAP_MS);
    }
  }

  /** The race is live underneath (first snapshot) — the fade may begin once
   *  the minimum visible time has also passed. */
  raceReady(): void {
    this.raceStarted = true;
    if (!this.onRelease) this.released = true; // online: the race is live
    this.paintStatus();
    this.maybeFade();
  }

  private maybeFade(): void {
    if (this.fading || this.root.style.display === 'none') return;
    if (!this.minTimeReached || !this.raceStarted) return;
    if (this.onRelease && !this.released) return; // offline: never fade before the start
    this.fading = true;
    this.root.classList.add('raceintro-out');
    this.fadeTimer = window.setTimeout(() => this.hide(), INTRO_FADE_MS);
  }

  private stopGlobe(): void {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.spinner?.dispose();
    this.spinner = null;
    this.renderer?.domElement.remove(); // keep the renderer, drop it from the DOM
  }

  private clearTimers(): void {
    if (this.hideTimer !== null) window.clearTimeout(this.hideTimer);
    if (this.fadeTimer !== null) window.clearTimeout(this.fadeTimer);
    if (this.failTimer !== null) window.clearTimeout(this.failTimer);
    if (this.readyTimer !== null) window.clearTimeout(this.readyTimer);
    this.hideTimer = null;
    this.fadeTimer = null;
    this.failTimer = null;
    this.readyTimer = null;
  }

  hide(): void {
    this.clearTimers();
    this.stopGlobe();
    this.onRelease = null;
    const wasVisible = this.root.style.display !== 'none';
    this.root.style.display = 'none';
    if (wasVisible) this.onVisibleChange?.(false);
  }

  dispose(): void {
    this.hide();
    this.renderer?.dispose();
    this.renderer = null;
    this.root.remove();
  }
}
